import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { BookOpen, PlayCircle } from "lucide-react";

export default function Library() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/library").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO title="My Library" description="Your purchased books and courses." noindex />
      <section className="border-b border-brand-line">
        <div className="container-narrow py-16 md:py-24">
          <span className="overline text-brand-rust">Your space</span>
          <h1 className="font-display text-5xl md:text-6xl mt-4 text-brand-ink">My Library</h1>
          <p className="mt-4 text-brand-inkMuted max-w-2xl">Everything you've purchased, in one place. Pick up where you left off, or queue something new.</p>
        </div>
      </section>

      <section className="container-narrow py-14">
        {loading ? (
          <div className="overline text-brand-inkMuted py-16 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-brand-line bg-white p-16 text-center" data-testid="library-empty">
            <p className="font-serif text-2xl text-brand-ink">No items yet.</p>
            <p className="text-sm text-brand-inkMuted mt-2">Your purchased books and courses will live here.</p>
            <Link to="/books" className="inline-block mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide">Browse books</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p) => {
              const isCourse = p.type === "course";
              const to = isCourse ? `/learn/${p.product_id}` : `/p/${p.product_id}`;
              return (
                <Link key={p.product_id} to={to} data-testid={`library-item-${p.product_id}`} className="group bg-white border border-brand-line hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex">
                  <div className="w-32 h-40 flex-none overflow-hidden bg-brand-bg">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="overline text-brand-inkMuted">{p.category}</span>
                    <p className="font-serif text-lg text-brand-ink mt-2 leading-snug">{p.title}</p>
                    <p className="text-sm text-brand-inkMuted">{p.author || p.instructor}</p>
                    <span className="mt-auto pt-3 overline text-brand-rust flex items-center gap-1">
                      {isCourse ? <PlayCircle className="w-3 h-3" /> : <BookOpen className="w-3 h-3" />}
                      {isCourse ? "Continue" : "Read"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </Layout>
  );
}
